import { isEqual, uniqWith } from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import reset from '~/assets/icons/reset.svg';
import { playSound, Sound } from '~/assets/sounds';
import {
	PropertyContainer,
	PropertyContainerRow,
} from '~/components/ui/PropertyContainer';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { HandlingMode, InputAction } from '~/shared';
import { useModal } from '../../hooks';
import {
	setModeValue,
	setUnsavedChanges,
	showSaveModal,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Divider } from '../Divider';
import {
	handlingIcons,
	modeList,
	techTuningSectionName,
} from './Settings.model';
import {
	StyledContainer,
	StyledPopupContainer,
	StyledPresetButtonContainer,
	StyledPresetsContainer,
	StyledResetButton,
	StyledSettingsCategoryHead,
	StyledSettingsCategoryName,
	StyledSettingsContainer,
} from './Settings.styled';

const Settings: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const {
		handleSetupNewPresetModal,
		handleResetAllModal,
		handleResetModal,
		handleUnsavedChangesModeModal,
	} = useModal();
	const mode = useLayoutSelector((state) => state.handling.mode);
	const handling = useObservable(rage.handlingTuning.getHandling());
	const handlingTuning = useObservable(rage.handlingTuning.getParameters());
	const modeSub = useObservable(rage.vehicles.getLocalVehicleHandlingMode());
	const modalState = useObservable(rage.modal.getCurrentModal());
	const [popupVisible, setPopupVisible] = useState<boolean>(false);

	const handleSetMode = useCallback(
		(mode: HandlingMode) => {
			handleUnsavedChangesModeModal(mode);
		},
		[handleUnsavedChangesModeModal],
	);

	useInputAction(InputAction.CommonMenuAccept, handleSetupNewPresetModal);

	useInputAction(InputAction.HandlingSave, () => {
		if (modalState) {
			return;
		}

		dispatch(showSaveModal());
	});

	useEffect(() => {
		dispatch(setModeValue(modeSub));
	}, [modeSub]);

	const handlingSections = useMemo(
		() =>
			uniqWith(
				handlingTuning.map((e) => [
					e.section,
					techTuningSectionName[e.section],
				]),
				isEqual,
			),
		[handlingTuning],
	);

	return (
		<StyledContainer>
			<CSSTransition
				in={popupVisible}
				timeout={300}
				classNames={'popup'}
				unmountOnExit
			>
				<StyledPopupContainer>
					<Text>Сбросить все</Text>
				</StyledPopupContainer>
			</CSSTransition>
			<StyledPresetsContainer>
				{modeList.map((item, index) => (
					<StyledPresetButtonContainer
						$active={mode === item.name}
						onClick={() => {
							handleSetMode(item.name);
							playSound(Sound.ClickButton);
						}}
						key={index}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
					>
						<Text>{item.title}</Text>
					</StyledPresetButtonContainer>
				))}
				<StyledPresetButtonContainer
					onClick={() => {
						handleResetAllModal();
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => {
						playSound(Sound.LinkCursor);
						setPopupVisible(true);
					}}
					onMouseLeave={() => setPopupVisible(false)}
				>
					<img src={reset} />
				</StyledPresetButtonContainer>
			</StyledPresetsContainer>
			<Divider $direction={'horizontal'} $top={20} $bottom={30} />
			<StyledSettingsContainer tabIndex={-1}>
				{handlingSections.map(([section, sectionName], index) => (
					<Fragment key={index}>
						<StyledSettingsCategoryHead
							style={{ marginTop: index > 0 ? 30 : 0 }}
						>
							<StyledSettingsCategoryName>
								<img
									src={handlingIcons[section as keyof typeof handlingIcons]}
								/>
								<Text>{sectionName}</Text>
							</StyledSettingsCategoryName>
						</StyledSettingsCategoryHead>
						<PropertyContainer style={{ marginTop: 20 }}>
							{handlingTuning
								.filter(
									(e) =>
										e.section === section &&
										typeof handling[e.property] === 'number',
								)
								.map((property, index, arr) => {
									const propIndex = handlingTuning.findIndex(
										(e) => e.property === property.property,
									);
									return (
										<Fragment key={`prop#${propIndex}`}>
											<PropertyContainerRow>
												<Text>{property.name}</Text>
												<StyledResetButton
													onClick={() => {
														handleResetModal(property.name, property.property);
														playSound(Sound.ClickButton);
													}}
													onMouseEnter={() => playSound(Sound.LinkCursor)}
												>
													Сбросить
												</StyledResetButton>
											</PropertyContainerRow>
											<PropertyContainerRow>
												<Slider
													min={property.limits[0]}
													max={property.limits[1]}
													step={property.step}
													value={handling[property.property] as number}
													width={300}
													onChange={(newValue) => {
														const newHandling = {
															...handling,
															[property.property]: newValue,
														};
														dispatch(setUnsavedChanges(true));
														rage.handlingTuning.setHandling(newHandling);
													}}
													valueInput
													center
												/>
											</PropertyContainerRow>
											{index < arr.length - 1 && (
												<Divider
													$direction={'horizontal'}
													$top={0}
													$bottom={0}
												/>
											)}
										</Fragment>
									);
								})}
						</PropertyContainer>
					</Fragment>
				))}
			</StyledSettingsContainer>
		</StyledContainer>
	);
};

export { Settings };
